import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Megaphone, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BroadcastPanel = () => {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!content.trim()) return;
    if (!window.confirm('Send this message to every user, each in their own private support thread?')) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/superadmin/broadcast`,
        { content: content.trim() },
        { withCredentials: true }
      );
      toast.success(data.message || 'Broadcast delivered');
      setContent('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to broadcast');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="broadcast-panel">
      <div>
        <h2 className="text-xl font-heading font-semibold">Broadcast</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Send one message that lands in every user's private support thread.
          There is no shared user group — each user sees the message as a 1:1 note from support.
        </p>
      </div>

      <div className="p-4 bg-secondary/10 border border-secondary/20 rounded-lg flex items-start gap-3">
        <Warning className="w-5 h-5 text-secondary shrink-0 mt-0.5" weight="fill" />
        <p className="text-sm text-foreground/80">
          Broadcasts are delivered immediately and audit-logged. They cannot be recalled
          from users' threads.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="broadcast-content">Message</Label>
        <Textarea
          id="broadcast-content"
          rows={5}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="e.g. Scheduled maintenance on Saturday at 02:00 UTC..."
          maxLength={4000}
          data-testid="broadcast-content-input"
        />
        <p className="text-xs text-muted-foreground text-right">{content.length} / 4000</p>
      </div>

      <Button
        onClick={handleSend}
        disabled={sending || !content.trim()}
        className="bg-primary hover:bg-primary/90"
        data-testid="send-broadcast-button"
      >
        <Megaphone className="w-4 h-4 mr-2" weight="fill" />
        {sending ? 'Broadcasting...' : 'Broadcast to all users'}
      </Button>
    </div>
  );
};

export default BroadcastPanel;
